<template>
  <v-row>
    <!-- Date Filter -->
    <v-col cols="12" class="pb-0">
      <v-card tile flat class="pa-2" id="documents-note-text">
        <!-- View Documents for Last... -->
        <v-card-text id="please-note-verbiage">
          <p class="text-body-1">{{ $t('L_view_documents_for_last') }}</p>
          <OnexSelect
            class="py-2"
            max-width="200"
            v-model="viewFilter"
            :items="viewFilterItems"
            :label="$t('')"
            :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
          ></OnexSelect>
          <p class="text-body-1">{{ $t('L_please_note_that_some_documents') }}</p>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" class="pt-0">
      <v-card tile align-stretch flat v-if="docs.length > 0">
        <v-data-table :headers="headers" :items="docs" hide-default-footer>
          <template v-slot:item="props">
            <tr class="text-no-wrap">
              <td>{{ props.item.date }}</td>
              <td>{{ $t(`L_${props.item.type}`) }}</td>
              <td class="cursor-pointer">
                <a @click="getDocumentPDF(props.item)">{{ props.item.name }}</a>
              </td>
              <td>
                <v-icon @click="downloadPDF(props.item)" color="#052f60" :icon="mdiDownload" />
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
      <v-card flat v-else>
        <v-card-text>
          <div class="py-5">
            <v-img
              id="documents-folder-img"
              height="180"
              width="180"
              src="@/assets/documentsFolder.png"
              alt="documents-folder"
              class="d-flex mx-auto"
            />
          </div>
          <div id="you-have-no-available" class="font-weight-bold text-center">
            {{ $t('L_no_documents_currently_available') }}
          </div>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" class="container" />
  </v-row>
</template>

<script lang="ts">
import { openInNewWindow } from '@/mixins/openNewWindow';
import { formatDate as formatDateUtil } from '@/shared/util/formatters';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { type ComponentOptions, defineComponent } from 'vue';
import { formatDate, subDays, isAfter, isEqual } from 'date-fns';
import { mdiDownload } from '@mdi/js';
import { OnexSelect } from '@primerica/onex-ui-lib';
import type { IGlobalStoreDocumentsItem } from '@/stores/global/globalStore.model';
import type { IDocumentMetadata } from '@/shared/api/models.content';

const componentOptions: ComponentOptions = {
  name: 'NoticesAndConfirmations',
  components: {
    OnexSelect,
  },
  data() {
    return {
      viewFilter: 30,
      mdiDownload,
    };
  },
  mixins: [openInNewWindow],
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, {
      dateFormat: 'getDateFormat',
      currentLocale: 'getLocale',
      noticesAndConfirmations: 'noticesAndConfirmations',
      base64String: 'base64String',
    }),
    filteredNoticesAndConfirmations() {
      const cutoffDate = subDays(new Date(), +this.viewFilter);
      const filteredDocumentsList: IGlobalStoreDocumentsItem[] =
        this.noticesAndConfirmations?.filter((doc: IDocumentMetadata) => {
          const isWithinCutoff =
            isAfter(new Date(doc.dateReceived), cutoffDate) ||
            isEqual(new Date(doc.dateReceived), cutoffDate);
          return isWithinCutoff;
        });
      return this.viewFilter === 0 ? this.noticesAndConfirmations : filteredDocumentsList;
    },
    headers() {
      return [
        {
          title: this.$t('L_date'),
          align: 'left',
          value: 'Date',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_type'),
          align: 'left',
          value: 'Type',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_name'),
          align: 'left',
          value: 'Name',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_download'),
          value: 'Download',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
      ];
    },
    viewFilterItems() {
      return [
        { title: this.$t('L_1_month'), value: 30 },
        { title: this.$t('L_3_months'), value: 90 },
        { title: this.$t('L_6_months'), value: 180 },
        { title: this.$t('L_all'), value: 0 },
      ];
    },
    docs() {
      const docs = this.filteredNoticesAndConfirmations;
      const r = docs?.map((doc: IGlobalStoreDocumentsItem) => {
        const labelForLocale = doc.labels
          ? doc.labels.find(
              (label: { language: string; name: string; type: string }) =>
                label.language === (this.currentLocale as string)
            )
          : null;
        const docsMeta = {
          date: doc.dateReceived
            ? formatDateUtil(formatDate(new Date(doc.dateReceived), 'yyyy-MM-dd'), this.dateFormat)
            : '',
          name: `${labelForLocale?.name} - ${doc.contract}`,
          type: labelForLocale?.type,
          doc: doc,
        };
        return docsMeta;
      });
      return r || [];
    },
  },
  methods: {
    async downloadPDF(item: {
      date: string;
      name: string;
      type: string;
      doc: IGlobalStoreDocumentsItem;
    }) {
      try {
        const docName = item.doc.name;
        const t = docName
          ? `view_doc_${docName[0].toLowerCase()}${docName.slice(1).replaceAll(' ', '')}`
          : '';
        const i = this.$dynatrace.enterAction(t);
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      // * see https://dev.to/nombrekeff/download-file-from-blob-21ho
      // Create a link pointing to the ObjectURL containing the blob
      let blobURL;
      if (item.doc.documentType === 'A') {
        blobURL = await this.getAllDocumentsUrl(item.doc.metadata);
      } else {
        blobURL = await this.getSingleDocumentUrl(item.doc.metadata);
      }
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = blobURL;
      tempLink.setAttribute('download', item.name);
      // Safari thinks _blank anchor are pop ups. We only want to set _blank
      // target if the browser does not support the HTML5 download attribute.
      // This allows you to download files in desktop safari if pop up blocking
      // is enabled.
      if (typeof tempLink.download === 'undefined') {
        tempLink.setAttribute('target', '_blank');
      }
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    },
    async getDocumentPDF(item: {
      date: string;
      name: string;
      type: string;
      doc: IGlobalStoreDocumentsItem;
    }) {
      try {
        const docName = item.doc.name;
        const t = docName
          ? `view_doc_${docName[0].toLowerCase()}${docName.slice(1).replaceAll(' ', '')}`
          : '';
        const i = this.$dynatrace.enterAction(t);
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      let url;
      if (item.doc.documentType === 'A') {
        url = await this.getAllDocumentsUrl(item.doc.metadata);
      } else {
        url = await this.getSingleDocumentUrl(item.doc.metadata);
      }
      const isWeb = CapacitorFacade.isWeb();
      await this.openInNewWindow({ url, inappbrowser: !isWeb });
    },
    // ***
    getSingleDocumentUrl(requestObject: IDocumentMetadata) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.globalStore.getOnePolicyDocument(requestObject).then(() => {
          const stringContainingURL = URL.createObjectURL(
            new Blob([this.base64String], { type: 'application/pdf' })
          );
          return stringContainingURL;
        });
      } else {
        return this.globalStore.getOnePolicyDocument(requestObject).then(() => this.base64String);
      }
    },
    // ***
    getAllDocumentsUrl(requestObject: IDocumentMetadata) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.globalStore.getAllPolicyDocuments(requestObject).then(() => {
          const stringContainingURL = URL.createObjectURL(
            new Blob([this.base64String], { type: 'application/pdf' })
          );
          return stringContainingURL;
        });
      } else {
        return this.globalStore.getAllPolicyDocuments(requestObject).then(() => this.base64String);
      }
    },
  },
  beforeRouteEnter(_, __, next) {
    // called before the route that renders this component is confirmed.
    // does NOT have access to `this` component instance,
    // because it has not been created yet when this guard is called!
    next((vm) => {
      // access to component public instance via `vm`
      try {
        const i = vm.$dynatrace.enterAction('view_documentsPage');
        vm.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    });
  },
  beforeRouteUpdate() {
    // called when the route that renders this component has changed,
    // but this component is reused in the new route.
    try {
      const i = this.$dynatrace.enterAction('view_documentsPage');
      this.$dynatrace.leaveAction(i);
    } catch (_error) {
      /* empty */
    }
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
table.v-table tbody td {
  font-size: 14px !important;
  padding: 24px !important;
}

.v-datatable.v-table thead th:first-child {
  padding-left: 24px !important;
}

#documents-note-text {
  border-bottom: solid 1px #ccc;
}

#please-note-verbiage {
  padding: 16px;
}
</style>
